<template>
  <div v-if="questions && questions.length" class="suggestion-list">
    <div v-for="(q, qi) in questions" :key="qi" class="suggestion-block">
      <div class="suggestion-block__question">
        <span class="suggestion-block__q-text">{{ q.question }}</span>
        <span v-if="isAnswered(q)" class="suggestion-block__answered">已答 ✓</span>
      </div>

      <div class="suggestion-block__options">
        <!-- 预置选项：多选，已选中的高亮；选「不确定」时互斥 -->
        <el-tag
          v-for="opt in q.options"
          :key="opt"
          class="suggestion-block__tag"
          :class="{
            'is-uncertain': opt === '不确定',
            'is-selected': isSelected(q, opt)
          }"
          :disable-transitions="true"
          effect="plain"
          @click="toggleOption(q, opt)"
        >{{ opt }}</el-tag>

        <!-- 已填的自定义答案：点击文字可修改，点 × 可删除 -->
        <span
          v-for="cv in customValuesOf(q)"
          :key="'custom-' + cv"
          class="suggestion-block__custom-item"
        >
          <el-tag
            class="suggestion-block__tag is-custom-answered"
            :disable-transitions="true"
            effect="plain"
            @click="editCustom(q, cv)"
          >{{ cv }} ✎</el-tag>
          <button
            type="button"
            class="suggestion-block__custom-remove"
            :title="`删除自定义答案：${cv}`"
            :aria-label="`删除自定义答案：${cv}`"
            @click.stop="removeCustom(q, cv)"
          >×</button>
        </span>

        <!-- 自定义入口 -->
        <el-tag
          v-if="customField !== q.field"
          class="suggestion-block__tag is-custom"
          :disable-transitions="true"
          effect="plain"
          @click="startCustom(q)"
        >＋ 自定义</el-tag>

        <!-- 自定义输入框 -->
        <div v-if="customField === q.field" class="suggestion-block__custom-row">
          <el-input
            v-model="customValue"
            size="small"
            maxlength="50"
            placeholder="输入你的答案"
            @keyup.enter="confirmCustom(q)"
          />
          <el-button link type="primary" size="small" @click="confirmCustom(q)">确定</el-button>
          <el-button link size="small" @click="cancelCustom">取消</el-button>
        </div>
      </div>
    </div>

    <div class="suggestion-list__actions">
      <el-button type="primary" @click="$emit('finish')">
        开始分析
      </el-button>
      <el-button plain @click="$emit('skip')">
        跳过问题，直接分析
      </el-button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  questions: { type: Array, default: () => [] },
  answers: { type: Object, default: () => ({}) }
})

const emit = defineEmits(['answer', 'finish', 'skip'])

const customField = ref(null)
const customValue = ref('')
// 正在编辑的那个自定义答案（null 表示新增；有值表示在改这一条）
const editingCustom = ref(null)

const UNCERTAIN = '不确定'

// 统一把答案读成数组：兼容历史单选字符串、以及后端可能回传的其他形态
function answerList(q) {
  const v = props.answers[q.field]
  if (v === undefined || v === null || v === '') return []
  if (Array.isArray(v)) return v.filter(x => x !== undefined && x !== null && x !== '')
  return [v]
}

// 所有问题都已作答（选「不确定」也算已答）
const allAnswered = computed(() =>
  props.questions.every((q) => answerList(q).length > 0)
)

function isAnswered(q) {
  return answerList(q).length > 0
}

function isSelected(q, opt) {
  return answerList(q).includes(opt)
}

// 属于自定义（不在预置选项里）的那部分答案
function customValuesOf(q) {
  const opts = Array.isArray(q.options) ? q.options : []
  return answerList(q).filter(v => !opts.includes(v))
}

// 多选切换：「不确定」与其他选项互斥
function toggleOption(q, opt) {
  if (customField.value === q.field) {
    customField.value = null
    customValue.value = ''
    editingCustom.value = null
  }
  const list = answerList(q)
  const selected = list.includes(opt)

  let next
  if (opt === UNCERTAIN) {
    // 选「不确定」→ 只留它；再点一次 → 取消
    next = selected ? [] : [UNCERTAIN]
  } else if (selected) {
    next = list.filter(v => v !== opt)
  } else {
    // 选普通选项时，把「不确定」摘掉
    next = [...list.filter(v => v !== UNCERTAIN), opt]
  }
  emit('answer', { field: q.field, value: next })
}

function startCustom(q) {
  customField.value = q.field
  customValue.value = ''
  editingCustom.value = null
}

function editCustom(q, value) {
  customField.value = q.field
  customValue.value = value
  editingCustom.value = value
}

// 删除某条自定义答案（预置选项不受影响）
function removeCustom(q, value) {
  const list = answerList(q).filter(v => v !== value)
  // 正在编辑的就是这条时，收起输入框
  if (editingCustom.value === value) {
    customField.value = null
    customValue.value = ''
    editingCustom.value = null
  }
  emit('answer', { field: q.field, value: list })
}

function confirmCustom(q) {
  const v = customValue.value.trim()
  if (!v) return
  const list = answerList(q)
  const editing = editingCustom.value
  customField.value = null
  customValue.value = ''
  editingCustom.value = null

  // 编辑已有自定义项：替换那一条；新增：追加（并摘掉「不确定」）
  const next = editing !== null
    ? list.map(x => (x === editing ? v : x))
    : [...list.filter(x => x !== UNCERTAIN && x !== v), v]
  emit('answer', { field: q.field, value: next })
}

function cancelCustom() {
  customField.value = null
  customValue.value = ''
  editingCustom.value = null
}
</script>

<style scoped lang="scss">
.suggestion-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.suggestion-block {
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  background: #f0f7ff;
  border: 1px solid #c8dff5;
  box-sizing: border-box;

  &__question {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #2c3e50;
    line-height: 1.5;
  }

  &__answered {
    font-size: 12px;
    color: #409EFF;
  }

  &__options {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  &__tag {
    cursor: pointer;
    font-size: 13px;
    padding: 6px 14px;
    border-radius: 16px;
    color: #2c3e50;
    background: #fff;
    border: 1px solid #a8c8e8;
    transition: all 0.2s;

    &:hover {
      color: #fff;
      background: #2c6eb0;
      border-color: #2c6eb0;
    }

    &.is-uncertain {
      border-style: dashed;
    }

    &.is-custom {
      border-style: dotted;
      color: #5a7a9a;
    }

    &.is-custom-answered {
      color: #fff;
      background: #2c6eb0;
      border-color: #2c6eb0;
      border-style: solid;
      cursor: pointer;
    }

    &.is-selected {
      color: #fff;
      background: #2c6eb0;
      border-color: #2c6eb0;
    }
  }

  /* 自定义答案 + 删除按钮：整体作为一个不可换行的小组 */
  &__custom-item {
    display: inline-flex;
    align-items: center;
    gap: 2px;
  }

  &__custom-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: #8a9bb0;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    transition: background-color 0.15s, color 0.15s;

    &:hover {
      background: #e8eef5;
      color: #c0392b;
    }
  }

  &__custom-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 280px;

    .el-input {
      flex: 1;
    }
  }
}

.suggestion-list__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  :deep(.el-button--primary) {
    background-color: #409EFF;
    border-color: #409EFF;

    &:hover {
      background-color: #2c6eb0;
      border-color: #2c6eb0;
    }

    &.is-disabled {
      background-color: #a0cfff;
      border-color: #a0cfff;
    }
  }
}
</style>
